import { type CheckboxProps, styled, Checkbox as TamaguiCheckbox, YStack } from "tamagui";
import { Check as CheckIcon, Minus as MinusIcon } from "@tamagui/lucide-icons";
import { radiusTokens } from "../themes/token-radius";
import { borderSizeTokens } from "../themes/token-space";

/**
 * Checkbox component with built-in styling and a checkmark icon
 */
const borderWidth = borderSizeTokens.border2;
const borderRadius = radiusTokens.br1;
const StyledCheckbox = styled(TamaguiCheckbox, {
  name: "Checkbox",
  position: "relative",
  width: "$si3",
  height: "$si3",
  borderRadius: borderRadius,
  borderWidth: borderWidth,
  overflow: "hidden",
  justifyContent: "center",
  alignItems: "center",
  padding: borderWidth,
  cursor: "pointer",
  backgroundColor: "$textPrimary",
  unstyled: true,
  variants: {
    checked: {
      true: {
        backgroundColor: "$textPrimary",
        borderColor: "$textPrimary"
      },
      indeterminate: {
        backgroundColor: "$textPrimary",
        borderColor: "$textPrimary"
      },
      false: {
        backgroundColor: "$textDisabled",
        borderColor: "$textDisabled"
      }
    },
    disabled: {
      true: {
        backgroundColor: "$uiIconFill",
        borderColor: "$uiIconFill"
      }
    }
  } as const,
  focusStyle: {
    outlineWidth: borderSizeTokens.border3,
    outlineColor: "$uiIconFill",
    outlineStyle: "solid"
  },
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "bouncy",
  animateOnly: ["borderColor"]
});
const Inner = styled(YStack, {
  name: "CheckboxInner",
  position: "absolute",
  inset: 0,
  borderRadius: borderRadius - borderWidth,
  backgroundColor: "$surfaceWhite",
  variants: {
    checked: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    indeterminate: {
      true: {
        scale: 0
      },
      false: {
        scale: 1
      }
    },
    disabled: {
      true: {
        backgroundColor: "$surfaceSecondary"
      }
    }
  } as const,
  defaultVariants: {
    checked: false,
    disabled: false
  },
  transition: "quick"
});
const StyledCheckIcon = styled(CheckIcon, {
  size: "$iconSmall",
  color: "$surfaceWhite",
  variants: {
    disabled: {
      true: {
        color: "$surfaceSecondary"
      },
      false: {
        color: "$surfaceWhite"
      }
    }
  } as const,
  defaultVariants: {
    disabled: false
  }
});
const StyledMinusIcon = styled(MinusIcon, {
  size: "$iconSmall",
  color: "$surfaceWhite",
  variants: {
    disabled: {
      true: {
        color: "$surfaceSecondary"
      },
      false: {
        color: "$surfaceWhite"
      }
    }
  } as const,
  defaultVariants: {
    disabled: false
  }
});
export const Checkbox = ({
  checked,
  ...props
}: CheckboxProps) => {
  const isIndeterminate = checked === "indeterminate";
  const isMarked = checked === true || isIndeterminate;
  return <StyledCheckbox checked={checked} transition={["bouncy", {
    delay: 500
  }]} {...props} data-sentry-element="StyledCheckbox" data-sentry-component="Checkbox" data-sentry-source-file="Checkbox.tsx">
      <Inner checked={isMarked} disabled={props.disabled} transition={[isMarked ? "quick" : "bouncy", {
      delay: !isMarked ? 0 : 120
    }]} data-sentry-element="Inner" data-sentry-source-file="Checkbox.tsx" />
      <YStack justifyContent="center" alignItems="center" scale={isMarked ? 1 : 0.7} opacity={isMarked ? 1 : 0} transition={[isMarked ? "bouncy" : "quick", {
      delay: isMarked ? 120 : 0
    }]} animateOnly={["transform", "opacity"]} pointerEvents="none" data-sentry-element="YStack" data-sentry-source-file="Checkbox.tsx">
        {isIndeterminate ? <StyledMinusIcon disabled={props.disabled} /> : <StyledCheckIcon disabled={props.disabled} />}
      </YStack>
    </StyledCheckbox>;
};